/**
 * Inbox (docs/FRONTEND-PLAN.md §2.3) — the upgraded /notifications page.
 *
 * Active/Dismissed tabs + a type filter, cursor pagination via
 * `usePagedList`, click-through that marks a notification read and
 * navigates its (internal) `actionUrl`, and — for admins only — a composer
 * for the two notification types the backend lets a person author directly
 * (`general_announcement`, `escalation_event`; everything else is emitted
 * in-process by other services). Every mutation calls the notifications
 * context's `refresh()` so the nav badge never drifts from the server.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as notificationsApi from '../api/notifications';
import { usePagedList } from '../lib/usePagedList';
import { useNotifications } from '../lib/notifications';
import { useAuth } from '../lib/auth';
import { internalPath } from '../lib/safePath';
import { useToast } from '../components/toast';
import Tabs from '../components/Tabs';
import Modal from '../components/Modal';
import {
  ErrorAlert,
  Spinner,
  EmptyState,
  PagedListFooter,
  SelectField,
  Field,
  TextareaField,
  RelativeTime,
  StatusPill
} from '../components/ui';
import { ui, cn } from '../lib/ui';

const PAGE_SIZE = 25;

const TABS = [
  { id: 'active', label: 'Active' },
  { id: 'dismissed', label: 'Dismissed' }
];

// AppNotification.TYPES (src/shared/models/AppNotification.js), humanized
// for the filter and the row pill.
const TYPES = [
  { id: 'new_task', label: 'New task' },
  { id: 'task_status_update', label: 'Task status update' },
  { id: 'new_question', label: 'New question' },
  { id: 'new_request', label: 'New request' },
  { id: 'new_response', label: 'New response' },
  { id: 'new_answer', label: 'New reply' },
  { id: 'new_event', label: 'New event' },
  { id: 'escalation_event', label: 'Escalation' },
  { id: 'general_announcement', label: 'Announcement' },
  { id: 'new_match', label: 'New match' },
  { id: 'billing_event', label: 'Billing' },
  { id: 'payment_issue', label: 'Payment issue' }
];
const TYPE_LABELS = Object.fromEntries(TYPES.map((t) => [t.id, t.label]));

// The backend's handleCreate gate: an admin-authored notification can only
// be one of these two — lifecycle types are only ever emitted in-process.
const COMPOSER_TYPES = TYPES.filter(
  (t) => t.id === 'general_announcement' || t.id === 'escalation_event'
);
const PRIORITIES = ['low', 'medium', 'high', 'urgent'];

const EMPTY_COMPOSER = {
  recipients: '',
  type: 'general_announcement',
  priority: 'medium',
  subject: '',
  message: '',
  actionUrl: ''
};

/** Comma/space-separated user IDs → a trimmed, deduplicated, order-preserving list. */
function parseRecipients(text) {
  const seen = new Set();
  const ids = [];
  text
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .forEach((id) => {
      if (!seen.has(id)) {
        seen.add(id);
        ids.push(id);
      }
    });
  return ids;
}

/** Clickable iff there's something for the click to do. */
function isClickable(notification) {
  return Boolean(!notification.readAt || internalPath(notification.actionUrl));
}

export default function Notifications() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refresh: refreshBadge } = useNotifications();
  const toast = useToast();
  // useAuth() exposes isAccredited/isReviewer but no admin flag — derive it
  // locally rather than touching lib/auth.jsx (out of scope for this finding).
  const isAdmin = Boolean(user && user.role === 'admin');

  const [tab, setTab] = useState('active');
  const [type, setType] = useState('');
  const [markingAll, setMarkingAll] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [composer, setComposer] = useState(EMPTY_COMPOSER);
  const [composerError, setComposerError] = useState(null);
  const [composerBusy, setComposerBusy] = useState(false);

  const list = usePagedList(
    (cursor) =>
      notificationsApi
        .listNotifications({
          status: tab,
          type: type || undefined,
          limit: PAGE_SIZE,
          lastEvaluatedKey: cursor
        })
        .then((d) => ({ items: d.notifications || [], lastEvaluatedKey: d.lastEvaluatedKey })),
    [tab, type]
  );

  async function markRead(notification) {
    if (notification.readAt) return;
    try {
      await notificationsApi.markRead(notification.id);
      list.mutateItem(notification.id, { readAt: new Date().toISOString() });
      refreshBadge();
    } catch (err) {
      toast.error(err);
    }
  }

  async function dismiss(notification, event) {
    event.stopPropagation();
    try {
      await notificationsApi.dismiss(notification.id);
      list.removeItem(notification.id);
      refreshBadge();
    } catch (err) {
      toast.error(err);
    }
  }

  async function markAllRead() {
    setMarkingAll(true);
    try {
      const { markedRead } = await notificationsApi.markAllRead();
      toast.success(`Marked ${markedRead} notification${markedRead === 1 ? '' : 's'} as read.`);
      list.reload();
      refreshBadge();
    } catch (err) {
      toast.error(err);
    } finally {
      setMarkingAll(false);
    }
  }

  function openNotification(notification) {
    if (!notification.readAt) markRead(notification);
    // `internalPath`, not `startsWith('/')`: `/\evil.example` passes the
    // latter and resolves off-origin (SEC-27).
    const actionUrl = internalPath(notification.actionUrl);
    if (actionUrl) {
      navigate(actionUrl);
    }
    // External or absent actionUrl: marking read (above) is the whole job —
    // never window.open an untrusted URL from a notification payload.
  }

  function openComposer() {
    setComposer(EMPTY_COMPOSER);
    setComposerError(null);
    setComposerOpen(true);
  }

  function closeComposer() {
    setComposerOpen(false);
    setComposerError(null);
  }

  async function submitComposer(event) {
    event.preventDefault();
    const recipients = parseRecipients(composer.recipients);
    if (recipients.length === 0) {
      setComposerError(new Error('Enter at least one recipient user ID.'));
      return;
    }
    if (!composer.subject.trim() || !composer.message.trim()) {
      setComposerError(new Error('Subject and message are required.'));
      return;
    }
    setComposerBusy(true);
    setComposerError(null);
    try {
      await notificationsApi.create({
        recipients,
        type: composer.type,
        priority: composer.priority,
        subject: composer.subject.trim(),
        message: composer.message.trim(),
        actionUrl: composer.actionUrl.trim() || undefined
      });
      toast.success(
        `Announcement sent to ${recipients.length} recipient${recipients.length === 1 ? '' : 's'}.`
      );
      closeComposer();
    } catch (err) {
      setComposerError(err);
      toast.error(err);
    } finally {
      setComposerBusy(false);
    }
  }

  return (
    <>
      <div className={cn(ui.row, ui.spread, 'mb-4')}>
        <h1 className="m-0 text-title-l">Inbox</h1>
        <div className={ui.row}>
          {isAdmin && (
            <button type="button" className={ui.btnSecondary} onClick={openComposer}>
              New announcement
            </button>
          )}
          <button type="button" className={ui.btnPrimary} onClick={markAllRead} disabled={markingAll}>
            {markingAll ? 'Marking…' : 'Mark all read'}
          </button>
        </div>
      </div>

      <div className={cn(ui.row, ui.spread, 'mb-4')}>
        <Tabs
          tabs={TABS}
          value={tab}
          onChange={(id) => setTab(id)}
          idPrefix="inbox-tab"
          aria-label="Filter by status"
        />
        <div className="min-w-52">
          <SelectField label="Type" value={type} onChange={(e) => setType(e.target.value)}>
            <option value="">All types</option>
            {TYPES.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </SelectField>
        </div>
      </div>

      <div role="tabpanel" id="inbox-tab-panel" aria-labelledby={`inbox-tab-${tab}`}>
        <ErrorAlert error={list.error} />

        {list.loading ? (
          <Spinner />
        ) : list.items.length === 0 ? (
          !list.error && (
            <EmptyState>
              {tab === 'active' ? "You're all caught up." : 'No dismissed notifications.'}
            </EmptyState>
          )
        ) : (
          list.items.map((n) => {
            const unread = !n.readAt;
            const clickable = isClickable(n);
            return (
              <div
                key={n.id}
                className={cn(clickable ? ui.cardClickable : ui.card, 'mb-4')}
                role={clickable ? 'button' : undefined}
                tabIndex={clickable ? 0 : undefined}
                onClick={clickable ? () => openNotification(n) : undefined}
                onKeyDown={
                  clickable
                    ? (event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          openNotification(n);
                        }
                      }
                    : undefined
                }
              >
                <div className={cn(ui.row, ui.spread)}>
                  <span className={ui.row}>
                    {unread && <span className={ui.unreadDot} aria-hidden="true" />}
                    {unread ? (
                      <strong>{n.subject || 'Notification'}</strong>
                    ) : (
                      <span>{n.subject || 'Notification'}</span>
                    )}
                  </span>
                  <RelativeTime iso={n.createdAt} />
                </div>
                <div className={cn(ui.row, 'mt-4')}>
                  <span className={ui.pillNeutral}>{TYPE_LABELS[n.type] || n.type}</span>
                  {(n.priority === 'high' || n.priority === 'urgent') && (
                    <StatusPill status={n.priority} />
                  )}
                </div>
                {n.message && <p>{n.message}</p>}
                <div className={ui.row}>
                  {unread && (
                    <button
                      type="button"
                      className={ui.btnSecondary}
                      onClick={(event) => {
                        event.stopPropagation();
                        markRead(n);
                      }}
                    >
                      Mark read
                    </button>
                  )}
                  {tab === 'active' && (
                    <button
                      type="button"
                      className={ui.btnSecondary}
                      onClick={(event) => dismiss(n, event)}
                    >
                      Dismiss
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}

        <PagedListFooter
          hasMore={list.hasMore}
          loadingMore={list.loadingMore}
          onLoadMore={list.loadMore}
          endLabel={list.items.length ? 'No more notifications.' : null}
        />
      </div>

      <Modal open={composerOpen} onClose={closeComposer} title="New announcement">
        <form onSubmit={submitComposer}>
          <ErrorAlert error={composerError} />
          <TextareaField
            label="Recipients"
            hint="User IDs, separated by commas or spaces."
            rows={2}
            required
            value={composer.recipients}
            onChange={(e) => setComposer((c) => ({ ...c, recipients: e.target.value }))}
          />
          <SelectField
            label="Type"
            value={composer.type}
            onChange={(e) => setComposer((c) => ({ ...c, type: e.target.value }))}
          >
            {COMPOSER_TYPES.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </SelectField>
          <SelectField
            label="Priority"
            value={composer.priority}
            onChange={(e) => setComposer((c) => ({ ...c, priority: e.target.value }))}
          >
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </SelectField>
          <Field
            label="Subject"
            required
            value={composer.subject}
            onChange={(e) => setComposer((c) => ({ ...c, subject: e.target.value }))}
          />
          <TextareaField
            label="Message"
            rows={4}
            required
            value={composer.message}
            onChange={(e) => setComposer((c) => ({ ...c, message: e.target.value }))}
          />
          <Field
            label="Action URL (optional)"
            hint="e.g. /forum/threads/123 — shown to recipients as a link-through."
            value={composer.actionUrl}
            onChange={(e) => setComposer((c) => ({ ...c, actionUrl: e.target.value }))}
          />
          <div className={cn(ui.row, 'mt-4')}>
            <button type="button" className={ui.btnSecondary} onClick={closeComposer}>
              Cancel
            </button>
            <button type="submit" className={ui.btnPrimary} disabled={composerBusy}>
              {composerBusy ? 'Sending…' : 'Send'}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}
